import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import Button from '@components/atoms/ui-kit/button.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useLiveTranscript } from '@utils/providers/use-live-transcript.ts';

type Props = { live: ReturnType<typeof useLiveTranscript>; embedded: boolean };
type SectionContext = {
  name: string;
  completed: boolean;
  visibleRows: number;
  totalRows: number;
  hiddenDirtyRows: number;
  busy: boolean;
  canEdit: boolean;
  onBack: () => void;
};
export const LiveTranscriptNotices = ({
  live,
  embedded,
  section
}: Props & { section?: SectionContext }) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const hasNotice = live.dirty || live.error || live.owner?.state === 'failed';
  const ingestionFailed = live.owner?.state === 'failed';
  return (
    <>
      <div className={embedded ? 'realtime-live-notices' : 'contents'}>
        {section && (
          <aside
            role="status"
            className="rounded-xl border p-3 text-sm flex gap-2 items-center"
            style={{
              borderColor: getColour('border'),
              backgroundColor: getColour('secondLayer')
            }}
          >
            <Button
              size="small"
              variant="outline"
              icon={<ArrowLeft size={14} />}
              label={t('live-full-event')}
              aria-label={t('live-back-to-entire-transcript')}
              disabled={section.busy}
              onClick={section.onBack}
              className="order-first shrink-0"
            />
            <span className="flex-1 min-w-0 overflow-x-auto whitespace-nowrap">
              <span className="font-medium">{section.name}</span>
              {' · '}
              {t('live-section-visible-count', {
                visible: section.visibleRows,
                total: section.totalRows
              })}
              {' · '}
              {t(
                section.completed
                  ? 'live-section-context-complete'
                  : section.canEdit
                    ? 'live-section-context-editing'
                    : 'live-section-context-read-only'
              )}
              {section.hiddenDirtyRows > 0 && (
                <span> {t('live-section-hidden-drafts')}</span>
              )}
            </span>
          </aside>
        )}
        {embedded && !hasNotice && !section && (
          <p className="realtime-live-hint">{t('live-edit-hint')}</p>
        )}
        {ingestionFailed && (
          <aside
            role="alert"
            className="rounded-xl border p-3 text-sm"
            style={{
              borderColor: getColour('border'),
              backgroundColor: getColour('secondLayer')
            }}
          >
            {t('live-ingestion-failed-guidance')}
          </aside>
        )}
        {live.dirty && (
          <aside
            role={live.draftStorageFailed ? 'alert' : 'status'}
            className="text-sm"
          >
            {t(
              live.draftStorageFailed
                ? 'live-draft-storage-failed'
                : 'live-draft-not-server-saved'
            )}
          </aside>
        )}
        {live.error && !(ingestionFailed && live.error === 'connection') && (
          <div
            role="alert"
            className="rounded-xl border p-3 flex flex-wrap gap-3 items-center"
            style={{
              borderColor: getColour('border'),
              backgroundColor: getColour('secondLayer')
            }}
          >
            <span className="flex-1">
              {t(
                live.error === 'connection'
                  ? 'live-viewer-disconnected-guidance'
                  : `live-${live.error}-error`
              )}
            </span>
            {live.error === 'conflict' ? (
              <Button
                variant="outline"
                size="small"
                label={t('live-reload-saved')}
                disabled={live.saving}
                onClick={() => {
                  void live.reloadSaved();
                }}
              />
            ) : (
              <Button
                variant="outline"
                size="small"
                label={t('retry')}
                onClick={() => {
                  void (live.error === 'save'
                    ? live.retrySave()
                    : live.retry());
                }}
              />
            )}
          </div>
        )}
      </div>
      {(embedded || live.partial) && (
        <aside
          className={`rounded-xl p-3 text-sm ${embedded ? 'realtime-live-partial' : ''}`}
          aria-label={t('live-partial')}
          style={{
            backgroundColor: getColour('secondLayer'),
            color: getColour('inactiveText')
          }}
        >
          <span className="font-medium">{t('live-partial')}: </span>
          {live.partial || t('live-waiting-for-speech')}
        </aside>
      )}
    </>
  );
};
